import { useCommunityContext } from 'providers';
import { useQuery, UseQueryResult } from 'react-query';

import { RewardAccount } from '@jebel/constants';

import { UserKeyFilter } from 'shared/graphql';
import { useHasSession } from 'shared/hooks';

export const USER_REWARD_ACCOUNT_QUERY_KEY = 'USER_REWARD_ACCOUNT';

/**
 * React hook to fetch a specific user's reward account for the selected community.
 *
 * Uses React Query to asynchronously retrieve reward account data from the tracking API.
 * The query is enabled only if there is an active session and a selected community.
 *
 * @param {UserKeyFilter} user - The user whose reward account should be fetched.
 * @returns {UseQueryResult<RewardAccount>} The query result containing the reward account data.
 * @throws {Error} If the user or selected community is not available, or if the fetch fails.
 */
export function useUserRewardAccount(
  user: UserKeyFilter | null | undefined,
): UseQueryResult<RewardAccount> {
  const hasSession = useHasSession();

  const { selectedCommunity } = useCommunityContext();

  return useQuery({
    enabled: Boolean(hasSession && selectedCommunity),
    queryKey: [USER_REWARD_ACCOUNT_QUERY_KEY, selectedCommunity?.id, user?.id],

    async queryFn() {
      const url = new URL(`/reward/account`, process.env.REACT_APP_TRACKING_API_URL);

      if (!user?.id) {
        throw new Error('No current user');
      }

      url.searchParams.set('user', user.id);

      if (!selectedCommunity?.id) {
        throw new Error('No selected community');
      }

      url.searchParams.set('community', selectedCommunity.id);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch current user reward account (HTTP ${response.status})`);
      }

      const data = await response.json();
      return data as RewardAccount;
    },
  });
}
